import { useState } from 'react';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { CopyButton } from '@components/copy-button';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconFormIndicatorError } from '@components/icons/form-indicator-error';
import { IconFormIndicatorInfo } from '@components/icons/form-indicator-info';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconRestart } from '@components/icons/restart';
import { IconTrash } from '@components/icons/trash';
import { InfoBox } from '@components/info-box';
import { RelativeSpinner } from '@components/loading-spinner/loading-spinner';
import { hideModal, Modal, useModal } from '@components/modal';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Stepper } from '@components/stepper';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { AttributeFieldType } from 'api/attributes/requests';
import { useGenerateInstructions } from 'api/email-triggers/hooks';
import { GeneratedFieldInstruction, JourneyEmailTrigger } from 'api/email-triggers/requests';
import { useWebSocket } from 'api/hooks';
import { AttributeIcon } from 'ui/authenticated/settings/pages/attributes/common';

export const useEmailTriggerListenModal = () => {
  return useModal(EmailTriggerListen, { variant: 'LARGE' });
};

type EmailTriggerListenProps = {
  trigger: JourneyEmailTrigger;
  onCreateAndSaveOutput: (fields: GeneratedFieldInstruction[]) => void;
};

type EmailReceivedContent = {
  event: 'email_received';
  logShortId: string;
  sender: string;
  subject: string;
  bodyPlain: string;
};

type ListenMessage = {
  content: EmailReceivedContent;
  type: string;
};

type ListenStep = 'send' | 'review';

const STEP_ORDER: ListenStep[] = ['send', 'review'];

export const EmailTriggerListen = ({ trigger, onCreateAndSaveOutput }: EmailTriggerListenProps) => {
  const { t } = useScopedTranslate(locale);
  const [step, setStep] = useState<ListenStep>('send');
  const [receivedEmail, setReceivedEmail] = useState<EmailReceivedContent | null>(null);
  const [generatedFields, setGeneratedFields] = useState<GeneratedFieldInstruction[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const generateMutation = useGenerateInstructions(trigger.shortId);

  const handleCreateAndSaveOutput = () => {
    if (generatedFields.length === 0) {
      return;
    }
    onCreateAndSaveOutput(generatedFields);
    showSuccess(t('fieldsPopulated'));
    hideModal();
  };

  const handleRemoveField = (attributeShortId: string) => {
    setGeneratedFields((prev) => {
      return prev.filter((field) => field.attributeShortId !== attributeShortId);
    });
  };

  useWebSocket(`email-trigger/listen/${trigger.shortId}/`, (data: MessageEvent<string>) => {
    const message = JSON.parse(data.data) as ListenMessage;

    if (message.content.event !== 'email_received') {
      return;
    }

    const email = message.content;
    if (receivedEmail) {
      return;
    }

    setReceivedEmail(email);
    setStep('review');

    generateMutation.mutate(
      { logShortId: email.logShortId },
      {
        onSuccess: (fields) => {
          setGeneratedFields(fields);
        },
        onError: () => {
          setErrorMessage(t('extractionFailed'));
        },
      },
    );
  });

  const handleRestart = () => {
    setReceivedEmail(null);
    setGeneratedFields([]);
    setErrorMessage('');
    setStep('send');
    generateMutation.reset();
  };

  const stepperItems = [{ label: t('sendStepLabel') }, { label: t('reviewStepLabel') }];

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Stack space={6}>
          <Stepper activeIndex={STEP_ORDER.indexOf(step)} items={stepperItems} />
          {step === 'send' ? (
            <SendStep
              triggerEmail={trigger.triggerEmail}
              infoText={t('sendStepInfo')}
              copyLabel={t('copyAddress')}
              sendLabel={t('sendTestEmail')}
              listeningTitle={t('listeningTitle')}
              listeningSubtitle={t('listeningSubtitle')}
            />
          ) : (
            <ReviewStep
              receivedEmail={receivedEmail}
              generatedFields={generatedFields}
              errorMessage={errorMessage}
              isExtracting={generateMutation.isLoading}
              onRemoveField={handleRemoveField}
            />
          )}
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        {step === 'send' ? (
          <Stack orientation="horizontal" justify="flex-end">
            <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
          </Stack>
        ) : (
          <Stack orientation="horizontal" justify="space-between" align="center">
            <Button
              text={t('restart')}
              variant="secondary"
              iconLeft={<IconRestart />}
              onClick={handleRestart}
            />
            <Stack orientation="horizontal" space={2}>
              <Button text={t('core.close')} variant="secondary" onClick={hideModal} />
              <Button
                text={t('createAndSaveOutput')}
                variant="primary-blue"
                onClick={handleCreateAndSaveOutput}
                isDisabled={
                  Boolean(errorMessage) ||
                  generateMutation.isLoading ||
                  generatedFields.length === 0
                }
              />
            </Stack>
          </Stack>
        )}
      </Modal.Footer>
    </Modal>
  );
};

type SendStepProps = {
  triggerEmail: string;
  infoText: string;
  copyLabel: string;
  sendLabel: string;
  listeningTitle: string;
  listeningSubtitle: string;
};

const SendStep = ({
  triggerEmail,
  infoText,
  copyLabel,
  sendLabel,
  listeningTitle,
  listeningSubtitle,
}: SendStepProps) => {
  return (
    <Stack space={6}>
      <InfoBox
        text={infoText}
        variant="indigo"
        icon={<IconFormIndicatorInfo color="elementIndigo200" />}
      />
      <Stack space={2}>
        <TextField label="" hideLabel defaultValue={triggerEmail} isDisabled readOnly />
        <Stack orientation="horizontal" space={2}>
          <CopyButton getText={() => triggerEmail} size="md" copyLabel={copyLabel} />
          <a href={`mailto:${triggerEmail}`}>
            <Button text={sendLabel} variant="secondary" color="textSecondary" />
          </a>
        </Stack>
      </Stack>
      <Separator />
      <Stack space={3} justify="center" align="center">
        <RelativeSpinner />
        <Stack space={1} align="center">
          <Text font="bold16" color="textPrimary" text={listeningTitle} center />
          <Text font="regular14" color="textSecondary" text={listeningSubtitle} center />
        </Stack>
      </Stack>
    </Stack>
  );
};

type ReviewStepProps = {
  receivedEmail: EmailReceivedContent | null;
  generatedFields: GeneratedFieldInstruction[];
  errorMessage: string;
  isExtracting: boolean;
  onRemoveField: (attributeShortId: string) => void;
};

const ReviewStep = ({
  receivedEmail,
  generatedFields,
  errorMessage,
  isExtracting,
  onRemoveField,
}: ReviewStepProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Stack space={4}>
      {errorMessage ? (
        <InfoBox variant="red" icon={<IconFormIndicatorError />} text={errorMessage} />
      ) : isExtracting ? (
        <Stack space={3} justify="center" align="center">
          <RelativeSpinner />
          <Text font="regular14" color="textSecondary" text={t('extracting')} center />
        </Stack>
      ) : (
        <InfoBox
          variant="green"
          icon={<IconFormIndicatorSuccess />}
          text={t('extractionSuccess')}
        />
      )}
      {receivedEmail?.bodyPlain && (
        <HideContainer title={t('emailBody')} defaultClosed>
          <Text
            font="regular14"
            color="textSecondary"
            text={receivedEmail.bodyPlain}
            style={{ whiteSpace: 'pre-wrap' }}
          />
        </HideContainer>
      )}
      {generatedFields.length > 0 && (
        <HideContainer title={t('extractedFields')}>
          <Stack space={2}>
            {generatedFields.map((field) => {
              return (
                <Card key={field.attributeShortId} spaceX={3} spaceY={2}>
                  <Stack orientation="horizontal" justify="space-between" align="center">
                    <Stack
                      orientation="horizontal"
                      space={2}
                      align="center"
                      style={{ overflow: 'hidden' }}
                    >
                      <AttributeIcon
                        type={field.attributeType as AttributeFieldType}
                        color="elementPurple50"
                      />
                      <Stack space={0}>
                        <Text font="bold14" text={field.label} ellipsisWithTooltip />
                        <Text
                          font="regular14"
                          color="textSecondary"
                          text={field.extractedValue || t('notExtracted')}
                          ellipsisWithTooltip
                        />
                      </Stack>
                    </Stack>
                    <IconButton
                      icon={<IconTrash color="elementPurple50" />}
                      variant="ghost-purple"
                      onClick={() => {
                        onRemoveField(field.attributeShortId);
                      }}
                    />
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        </HideContainer>
      )}
    </Stack>
  );
};

export const locale = {
  en: {
    title: 'Test email trigger',
    sendStepLabel: 'Send email',
    reviewStepLabel: 'Review extracted fields',
    sendStepInfo: 'Please note the trigger only matches existing fields',
    sendTestEmail: 'Send test email',
    copyAddress: 'Copy address',
    listeningTitle: 'Listening for incoming email...',
    listeningSubtitle: 'Send an email to the address above and it will appear here',
    emailBody: 'Email body',
    extracting: 'AI is extracting fields from the email body…',
    extractionSuccess: 'Fields extracted successfully',
    extractionFailed: 'Failed to extract fields from email',
    extractedFields: 'Extracted fields',
    notExtracted: 'No value extracted',
    restart: 'Restart',
    createAndSaveOutput: 'Create and save output',
    fieldsPopulated: 'Fields populated — remember to save',
  },
};
